// Calm UI kit: the shared pieces behind docs/ui-standard.md.
// Page > PageHeader > Stats (≤3) > StatusTabs + Toolbar > List of Rows | Empty.
import { useShellDark } from "@/lib/useShellDark";
import { T, F, th as palette, MainCtaScope, I } from "@/lib/bricks";
import BricksLoader from "@/components/BricksLoader";

export function usePalette() {
  // Same rule as the shell: a page drawn with night ink on a paper shell is unreadable.
  const dark = useShellDark();
  return { p: palette(dark), dark };
}

const CSS = `
.calm-stats { display: grid; grid-template-columns: repeat(var(--calm-n), minmax(0, 1fr)); gap: 12px; }
.calm-figure { font-size: clamp(17px, 4.8vw, 26px); }
@media (min-width: 640px) { .calm-stats { grid-template-columns: repeat(var(--calm-n), max-content); column-gap: 48px; } }
.calm-row { display: grid; grid-template-columns: minmax(0,1fr) auto; column-gap: 16px; row-gap: 4px; align-items: start; }
.calm-row > .calm-next { grid-column: 1 / -1; }
.calm-row > .calm-status { display: flex; gap: 10px; align-items: center; justify-content: flex-end; }
.calm-row[data-click="1"] { cursor: pointer; }
.calm-row[data-stack="1"] { grid-template-columns: minmax(0,1fr); }
.calm-row[data-stack="1"] > .calm-status { justify-content: flex-start; }
.calm-row[data-click="1"]:hover { background: var(--calm-hover); }
.calm-row:focus-visible { outline: 2px solid ${T.brick}; outline-offset: -2px; }
.calm-head { display: flex; flex-direction: column; gap: 14px; }
.calm-toolbar { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.calm-toolbar > .calm-search { flex: 1 1 200px; min-width: 0; }
.calm-table { width: 100%; }
@media (min-width: 768px) {
  .calm-row[data-wide="1"] { grid-template-columns: minmax(0,1.6fr) minmax(0,1fr) minmax(88px,auto); align-items: center; }
  .calm-row[data-wide="1"] > .calm-next { grid-column: auto; }
  .calm-row[data-wide="1"] > .calm-status { order: 3; text-align: right; justify-content: flex-end; }
  .calm-head { flex-direction: row; align-items: flex-end; justify-content: space-between; }
}
`;

export function CalmStyles() {
  return <style>{CSS}</style>;
}

/** Page container: readable width, room at the bottom for floating buttons. */
export function Page({ children, testid, width = 1040, style = {} }) {
  const { p } = usePalette();
  return (
    <div data-testid={testid} style={{
      display: "flex", flexDirection: "column", gap: 28, fontFamily: F.display, color: p.ink,
      maxWidth: width, width: "100%", minWidth: 0, paddingBottom: 96, "--calm-hover": p.surf2, ...style,
    }}>
      <CalmStyles />
      {children}
    </div>
  );
}

export const HELP_BASE = "https://www.brxs.ca/help";

/** Contextual "?" link to a help centre collection or article (opens in a new tab). */
export function HelpLink({ to, label = "Help", color, size = 18 }) {
  const { p } = usePalette();
  return (
    <a href={`${HELP_BASE}${to ? `/${to}` : ""}`} target="_blank" rel="noopener noreferrer"
      aria-label={`${label} (opens the help centre in a new tab)`} title={label} data-testid="help-link"
      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 44, height: 44, flexShrink: 0, borderRadius: 2, color: color || p.ink3 }}>
      <I name="help" size={size} color={color || p.ink3} />
    </a>
  );
}

export function PageHeader({ title, subtitle, action, help, helpLabel }) {
  const { p } = usePalette();
  return (
    <div className="calm-head">
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 2, marginLeft: 0 }}>
          <h1 style={{ fontSize: 30, fontWeight: 500, letterSpacing: -1, color: p.ink, margin: 0, lineHeight: 1.15 }}>{title}</h1>
          {help ? <span style={{ margin: "-8px 0" }}><HelpLink to={help} label={helpLabel || `Help with ${String(title).toLowerCase()}`} /></span> : null}
        </div>
        {subtitle ? <div style={{ fontSize: 13.5, color: p.ink3, marginTop: 6 }}>{subtitle}</div> : null}
      </div>
      {action ? <div style={{ display: "flex", gap: 8, flexWrap: "wrap", flexShrink: 0 }}><MainCtaScope>{action}</MainCtaScope></div> : null}
    </div>
  );
}

/** At most three plain figures. Extra items are dropped on purpose. */
export function Stats({ items, testid = "calm-stats" }) {
  const { p } = usePalette();
  const list = (items || []).filter(Boolean).slice(0, 3);
  if (!list.length) return null;
  return (
    <div data-testid={testid} className="calm-stats" style={{ "--calm-n": list.length }}>
      <CalmStyles />
      {list.map(({ label, value, attention }) => (
        <div key={label} style={{ minWidth: 0 }}>
          <div className="calm-figure" style={{ fontFamily: F.mono, letterSpacing: -0.5, lineHeight: 1, whiteSpace: "nowrap", color: attention ? p.attn : p.ink }}>{value}</div>
          <div style={{ fontSize: 12, color: p.ink3, marginTop: 8, lineHeight: 1.35 }}>{label}</div>
        </div>
      ))}
    </div>
  );
}

/** One compact underline status control. options: [{ key, label }]. Wraps on phones, never scrolls sideways. */
export function StatusTabs({ options, value, onChange, testid = "calm-status-filter", label = "Status", hit = false }) {
  const { p } = usePalette();
  return (
    <div className="calm-tabs" role="tablist" aria-label={label} data-testid={testid}
      style={{ display: "flex", flexWrap: "wrap", borderBottom: `1px solid ${p.hair}`, columnGap: 22, rowGap: 10 }}>
      {options.map((o) => {
        const on = value === o.key;
        return (
          <button key={o.key} type="button" role="tab" aria-selected={on} onClick={() => onChange(o.key)}
            style={{ background: "none", border: "none", padding: "0 0 10px", marginBottom: -1, cursor: "pointer", whiteSpace: "nowrap",
              fontFamily: F.display, fontSize: 14, fontWeight: on ? 500 : 400, color: on ? p.ink : p.ink3,
              borderBottom: `2px solid ${on ? p.ink : "transparent"}`,
              ...(hit ? { minHeight: 44, display: "inline-flex", alignItems: "flex-end", boxSizing: "border-box", paddingTop: 12 } : {}) }}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export const fieldStyle = (p) => ({
  padding: "9px 12px", borderRadius: 2, border: `1px solid ${p.hair}`, background: p.surf, color: p.ink,
  fontFamily: F.display, fontSize: 13, outline: "none", minHeight: 38, boxSizing: "border-box",
});

export function Search({ value, onChange, placeholder = "Search", testid }) {
  const { p } = usePalette();
  return (
    <input className="calm-search" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      aria-label={placeholder} data-testid={testid} style={fieldStyle(p)} />
  );
}

/** Small secondary filter. options: [{ key, label }]; the first is "all". */
export function SmallSelect({ value, onChange, options, label, testid, hit = false }) {
  const { p } = usePalette();
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label} data-testid={testid}
      style={{ ...fieldStyle(p), flex: "0 0 auto", maxWidth: "100%", cursor: "pointer", color: value === options[0]?.key ? p.ink3 : p.ink, ...(hit ? { minHeight: 44 } : {}) }}>
      {options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
    </select>
  );
}

export function Toolbar({ children }) {
  return <div className="calm-toolbar">{children}</div>;
}

/** Filters block: tabs over a search/select row, kept tight together. */
export function Controls({ children }) {
  return <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>{children}</div>;
}

/** Quiet inline parts joined by middots. parts: [{ text, figure, attention }] or strings. */
export function Meta({ parts, style = {} }) {
  const { p } = usePalette();
  const list = (parts || []).map((x) => (typeof x === "string" || typeof x === "number" ? { text: String(x) } : x)).filter((x) => x && x.text !== "" && x.text != null);
  if (!list.length) return null;
  return (
    <div style={{ fontSize: 12.5, color: p.ink3, minWidth: 0, overflowWrap: "anywhere", ...style }}>
      {list.map((it, i) => (
        <span key={i} style={{ color: it.attention ? p.attn : undefined }}>
          {i > 0 && <span style={{ margin: "0 7px", color: p.ink4 }}>·</span>}
          {it.figure ? <span style={{ fontFamily: F.mono, fontSize: 11.5 }}>{it.text}</span> : it.text}
        </span>
      ))}
    </div>
  );
}

/** Plain status word. attention → muted brick; otherwise quiet ink. */
/** Meta part: a mono figure followed by a plain label, e.g. figLabel("$4,200", "gross"). */
export function figLabel(value, label) {
  return { text: <><span style={{ fontFamily: F.mono, fontSize: 11.5 }}>{value}</span>{label ? ` ${label}` : ""}</> };
}

export function StatusText({ children, attention, strong, tag }) {
  const { p } = usePalette();
  if (tag) return (
    <span style={{ display: "inline-block", fontSize: 12, lineHeight: "18px", padding: "1px 8px", borderRadius: 2, whiteSpace: "nowrap",
      border: `1px solid ${attention ? p.attn : p.ink3}`, color: attention ? p.attn : p.ink2, fontWeight: 500 }}>
      {children}
    </span>
  );
  return (
    <span style={{ fontSize: 12.5, fontWeight: attention || strong ? 500 : 400, color: attention ? p.attn : p.ink2, whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
}

export function List({ children, testid }) {
  const { p } = usePalette();
  return <div data-testid={testid} style={{ borderTop: `1px solid ${p.hair}`, "--calm-hover": p.surf2 }}><CalmStyles />{children}</div>;
}

/**
 * One list row: title, one meta line, one next-step line, one status on the right.
 * Phones stack (title+meta | status, then next step full width); ≥768px it's three columns.
 * `stack` puts the status under the title on phones, drawn as a tag (stage labels).
 */
export function Row({ title, meta, next, status, attention, onClick, testid, aside, wide = true, stack = false }) {
  const { p } = usePalette();
  const click = typeof onClick === "function";
  return (
    <div className="calm-row" data-wide={wide && next ? "1" : "0"} data-stack={stack ? "1" : "0"} data-click={click ? "1" : "0"} data-testid={testid}
      role={click ? "button" : undefined} tabIndex={click ? 0 : undefined} onClick={onClick}
      onKeyDown={click ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(e); } } : undefined}
      style={{ padding: "16px 4px", borderBottom: `1px solid ${p.hair}` }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 500, color: p.ink, overflowWrap: "anywhere" }}>{title}</div>
        {meta ? (Array.isArray(meta) ? <Meta parts={meta} style={{ marginTop: 3 }} /> :
          <div style={{ fontSize: 12.5, color: p.ink3, marginTop: 3, overflowWrap: "anywhere" }}>{meta}</div>) : null}
      </div>
      <div className="calm-status">
        {status != null && status !== "" ? <StatusText attention={attention} tag={stack}>{status}</StatusText> : null}
        {aside}
      </div>
      {next ? <div className="calm-next" style={{ minWidth: 0 }}>{Array.isArray(next) ? <Meta parts={next} /> : typeof next === "string" ? <Meta parts={[next]} /> : next}</div> : null}
    </div>
  );
}

export function Empty({ children, testid = "calm-empty", action }) {
  const { p } = usePalette();
  return (
    <div data-testid={testid} style={{ padding: "40px 0", fontSize: 14, color: p.ink3, borderTop: `1px solid ${p.hair}` }}>
      {children}
      {action ? <div style={{ marginTop: 14 }}>{action}</div> : null}
    </div>
  );
}

/** Titled block. A heading line, then content. No box. */
export function Section({ title, action, children, testid, note }) {
  const { p } = usePalette();
  return (
    <section data-testid={testid} style={{ minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
        <h2 style={{ fontSize: 16, fontWeight: 500, letterSpacing: -0.2, margin: 0, color: p.ink }}>{title}</h2>
        {action ? <div style={{ fontSize: 13, flexShrink: 0 }}>{action}</div> : null}
      </div>
      {note ? <div style={{ fontSize: 12.5, color: p.ink3, marginTop: -4, marginBottom: 10 }}>{note}</div> : null}
      {children}
    </section>
  );
}

/** Quiet text link button for section actions ("View all"). */
export function TextLink({ children, onClick, href, testid, hit }) {
  const { p } = usePalette();
  // `hit` gives the link a full 44px touch target without changing how it reads.
  const style = { background: "none", border: "none", padding: 0, cursor: "pointer", color: p.ink2, fontFamily: F.display, fontSize: 13, textDecoration: "underline", textUnderlineOffset: 3, textDecorationColor: p.hairS,
    ...(hit ? { minHeight: 44, minWidth: 44, display: "inline-flex", alignItems: "center", justifyContent: "center" } : null) };
  if (href) return <a href={href} data-testid={testid} style={style}>{children}</a>;
  return <button type="button" onClick={onClick} data-testid={testid} style={style}>{children}</button>;
}

export function Loading() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 240 }}>
      <BricksLoader variant="assemble" size={40} color={T.brick} />
    </div>
  );
}

/** Field label for forms: sentence case, display type. */
export function FieldLabel({ children, htmlFor }) {
  const { p } = usePalette();
  return <label htmlFor={htmlFor} style={{ fontSize: 12.5, fontWeight: 500, color: p.ink3 }}>{children}</label>;
}

/** "in_progress" → "In progress"; leaves mixed-case labels alone. */
export function humanize(s) {
  if (s == null) return "";
  const t = String(s).replace(/[_-]+/g, " ").trim();
  if (!t) return "";
  if (t === t.toLowerCase() || t === t.toUpperCase()) return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
  return t;
}
